<template>
  <div class="richtext" :class="{ 'richtext--disabled': disabled }">
    <BubbleMenu v-if="editor" :editor="editor" :should-show="bubbleShouldShow">
      <div ref="bubble" class="richtext__bubble">
        <form
          class="richtext__bubble-form"
          v-show="['link', 'cta'].includes(bubbleState)"
          @submit.prevent="setLink(linkUrl)"
        >
          <input
            class="richtext__bubble-input"
            type="text"
            v-model="linkUrl"
            placeholder="https://"
            ref="linkInput"
            @keydown.esc="bubbleState = null"
          />
          <select v-if="bubbleState === 'cta'" v-model="bubbleOptions.theme">
            <option value="primary">{{ labels.primary }}</option>
            <option value="secondary">{{ labels.secondary }}</option>
          </select>
          <button @click="setLink(linkUrl)" :disabled="!linkUrl" type="button">
            <FontAwesomeIcon :icon="['far', 'check']" />
          </button>
          <button @click="setLink(null)" type="button">
            <FontAwesomeIcon :icon="['far', 'times']" />
          </button>
        </form>

        <template v-if="!bubbleState">
          <button
            v-for="(item, index) in getCommands('bubble')"
            :key="'bubble' + index"
            class="richtext__bubble-button"
            :class="{
              'is-active': editor.isActive(item.isActiveKey || item.stateKey, item.commandProps),
            }"
            :disabled="disabled || item.disabled"
            @click="
              item.customFunc
                ? item.customFunc()
                : editor.chain().focus()[item.commandKey](item.commandProps).run()
            "
            type="button"
          >
            <FontAwesomeIcon :icon="item.icon" />
          </button>
        </template>
      </div>
    </BubbleMenu>

    <EditorContent class="richtext__editor" ref="editor" :editor="editor" :editable="!disabled" />

    <div v-if="editor" class="richtext__menubar">
      <div class="richtext__menubar-buttons">
        <button
          v-for="(item, index) in getCommands('menu')"
          :key="'menubar' + index"
          type="button"
          :class="{
            'is-active': editor.isActive(item.isActiveKey || item.stateKey, item.commandProps),
          }"
          :disabled="disabled || item.disabled"
          @click="
            item.customFunc
              ? item.customFunc()
              : editor.chain().focus()[item.commandKey](item.commandProps).run()
          "
        >
          <FontAwesomeIcon :icon="item.icon" size="sm" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent, BubbleMenu } from '@tiptap/vue-2'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Superscript from '@tiptap/extension-superscript'
import CallToAction from './extensions/cta'
import StarterKit from '@tiptap/starter-kit'

const DefaultCommands = [
  'heading',
  'heading-h3',
  'bold',
  'italic',
  'superscript',
  'orderedList',
  'bulletList',
  'link',
  'cta',
]

import { Options } from '../index'

export { DefaultCommands }
export default {
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: Boolean,
    commands: {
      type: Array,
      default: () => DefaultCommands,
    },
    labels: {
      type: Object,
      default: () => Options.labels,
    },
  },
  watch: {
    value(value) {
      if (this.editor.getHTML() === value) return
      this.editor.commands.setContent(this.value, false)
    },
    disabled(value) {
      if (!this.editor) return
      this.editor.setOptions({ editable: !value })
    },
    bubbleState(value) {
      if (!value) return
      this.$refs.editor.$el.children[0].focus()
      setTimeout(() => this.$refs.linkInput.focus(), 1)
    },
  },
  data() {
    return {
      editor: null,
      linkUrl: null,
      bubbleState: null,
      bubbleOptions: { theme: 'primary' },
    }
  },
  computed: {
    availableCommands() {
      const language = this._i18n.locale

      return [
        {
          stateKey: 'heading',
          commandKey: 'toggleHeading',
          commandProps: { level: 2 },
          icon: ['far', 'h2'],
          appearance: {
            menu: true,
            bubble: false,
          },
        },
        {
          stateKey: 'heading-h3',
          isActiveKey: 'heading',
          commandKey: 'toggleHeading',
          commandProps: { level: 3 },
          icon: ['far', 'h3'],
          appearance: {
            menu: true,
            bubble: false,
          },
        },

        {
          stateKey: 'bold',
          commandKey: 'toggleBold',
          icon: ['far', language === 'fr' ? 'g' : 'bold'],
          appearance: {
            menu: true,
            bubble: true,
          },
        },
        {
          stateKey: 'italic',
          commandKey: 'toggleItalic',
          icon: ['far', 'italic'],
          appearance: {
            menu: true,
            bubble: true,
          },
        },
        {
          stateKey: 'orderedList',
          commandKey: 'toggleOrderedList',
          icon: ['far', 'list-ol'],
          appearance: {
            menu: true,
            bubble: false,
          },
        },
        {
          stateKey: 'bulletList',
          commandKey: 'toggleBulletList',
          icon: ['far', 'list-ul'],
          appearance: {
            menu: true,
            bubble: false,
          },
        },
        {
          stateKey: 'superscript',
          commandKey: 'toggleSuperscript',
          icon: ['far', 'superscript'],
          extension: Superscript,
          appearance: {
            menu: false,
            bubble: true,
          },
        },
        {
          stateKey: 'link',
          commandKey: '---',
          customFunc: () => {
            this.bubbleState = 'link'
          },
          disabled: this.editor && this.editor.state.selection.empty,
          icon: ['far', 'link'],
          extension: Link,
          appearance: {
            menu: true,
            bubble: true,
          },
        },
        {
          stateKey: 'image',
          commandKey: '---',
          customFunc: () => {},
          icon: ['far', 'image'],
          extension: Image,
          appearance: {
            menu: false,
            bubble: false,
          },
        },
        {
          stateKey: 'cta',
          commandKey: 'toggleCta',
          customFunc: () => {
            this.bubbleState = 'cta'
          },
          disabled: this.editor && this.editor.state.selection.empty,
          icon: ['far', 'mouse-pointer'],
          extension: CallToAction,
          appearance: {
            menu: true,
            bubble: true,
          },
        },
      ]
    },
  },
  methods: {
    getCommands(appearance) {
      return this.commands
        .map(cmd => this.availableCommands.find(a => a.stateKey === cmd))
        .filter(Boolean)
        .filter(cmd => !appearance || cmd.appearance[appearance])
    },
    setLink(value) {
      let editor = this.editor.chain().focus()
      switch (this.bubbleState) {
        case 'link':
          if (value)
            editor
              .setLink({ href: value, target: this.isInternalUrl(value) ? '_self' : '_blank' })
              .run()
          else editor.unsetLink().run()
          break
        case 'cta':
          if (value)
            editor
              .setCta({
                href: value,
                'data-cta': this.bubbleOptions.theme,
                target: this.isInternalUrl(value) ? '_self' : '_blank',
              })
              .run()
          else editor.unsetCta().run()
          break
      }

      this.bubbleState = null
      this.linkUrl = null
    },
    bubbleShouldShow({ editor }) {
      return (
        this.getCommands()
          .filter(a => a.appearance.bubble)
          .map(a => a.stateKey)
          .some(k => editor.isActive(k)) || !this.editor.state.selection.empty
      )
    },
    isInternalUrl(value) {
      if (!typeof value === 'string') return
      return ['/', '#'].some(v => value.startsWith(v))
    },
  },
  mounted() {
    this.editor = new Editor({
      editable: !this.disabled,
      extensions: [
        StarterKit,
        ...this.getCommands()
          .map(({ extension }) => extension)
          .filter(Boolean),
      ],
      content: this.value,
      onUpdate: () => {
        let html = this.editor.getHTML().replace(/^<p>\s*<\/p>$/, '')
        this.$emit('input', html)
      },
      onSelectionUpdate: ({ editor }) => {
        if (!this.bubbleShouldShow({ editor })) this.bubbleState = null
      },
    })
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy()
  },
  components: {
    EditorContent,
    BubbleMenu,
    // EditorMenuBar,
    // EditorMenuBubble,
  },
}
</script>

<style lang="scss">
@import './styles/_mixins';
.richtext {
  flex-grow: 1;
  display: flex;
  flex-direction: column-reverse;

  &--disabled {
    background-color: var(--vue-elder-input-color, #fafafa);
  }

  .tippy-content > div {
    visibility: visible !important; // Fix for visibility bug
  }

  &:not(.richtext--disabled):focus-within {
    outline: 1px solid var(--primary);
    border-radius: var(--border-radius, 3px);
  }

  h2 {
    font-size: 1.5em !important;
  }
  h3 {
    font-size: 1.17em !important;
    margin: 0;
  }

  &__editor {
    flex-grow: 1;

    .ProseMirror {
      padding: 0rem 1rem 1rem;
      min-height: 6rem;
      height: 100%;

      p:first-child {
        margin-top: 0;
      }

      li,
      ol {
        p {
          margin: 0;
        }
      }

      &:focus {
        outline: none;
      }

      img {
        max-width: 100%;
      }

      a[data-cta] {
        padding: 0.75em 1.5em;
        border-radius: var(--border-radius);
        text-decoration: none;

        display: inline-block;
      }

      a[data-cta='primary'] {
        background-color: var(--primary);
        color: var(--primary-contrast);
      }

      a[data-cta='secondary'] {
        background-color: var(--secondary);
        color: var(--secondary-contrast);
      }
    }
  }

  &__bubble {
    display: flex;
    background-color: var(--dark);
    color: white;
    border-radius: var(--border-radius);

    &-form {
      display: flex;

      button,
      select {
        background-color: transparent;
        color: inherit;
        border: none;
        border-left: 1px solid rgba(white, 0.1);
      }

      option {
        color: black;
      }

      button {
        padding: 0.5rem;
        min-width: 2.5rem;

        &:disabled {
          opacity: 0.5;
          background-color: rgba(white, 0.1);
        }
      }

      select {
        padding: 0.5rem !important;
      }
    }

    &-input {
      font: inherit;
      border: none;
      padding: 0.5rem;
      background-color: transparent;
      color: #fff !important;

      @include placeholder {
        color: rgba(white, 0.6) !important;
      }
    }

    &-button {
      width: initial;
      padding: 0.5rem 0.75rem !important;
      color: white;
      font-size: 0.6em;

      & + button {
        border-left: 1px solid rgba(white, 0.2) !important;
      }

      svg:nth-child(2) {
        margin-left: 0.5rem;
      }

      &:hover,
      &.is-active {
        background-color: rgba(white, 0.2) !important;
      }
    }
  }

  &__menubar {
    padding: 0.75rem;
    position: sticky;
    top: 0;
    z-index: 1;

    &-buttons {
      width: max-content;
      box-shadow: 0 2px 4px -2px rgba(black, 0.1);
    }

    button {
      padding: 0.5rem;
      min-width: 2.5rem;
      border: 1px solid var(--border-color);
      cursor: pointer;
      background-color: #ffffff;

      &:focus-visible {
        position: relative;
        border-radius: var(--border-radius, 3px);
        outline: 1px solid var(--primary) !important;
      }

      &:first-child {
        border-top-left-radius: var(--border-radius, 3px);
        border-bottom-left-radius: var(--border-radius, 3px);
      }

      &:last-child {
        border-top-right-radius: var(--border-radius, 3px);
        border-bottom-right-radius: var(--border-radius, 3px);
      }

      & + button {
        border-left: none;
      }

      &:disabled {
        cursor: not-allowed;
      }

      &:hover:not(:disabled),
      &.is-active {
        background-color: darken(#ffffff, 3%);
      }
    }
  }

  a {
    word-break: break-all;
  }

  .tippy-box {
    max-width: initial !important;
  }
}
</style>
